import { UserNavigation } from '../models/user-navigation.model';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { NavigationItem } from '../models/navigation-item.model';
import { NavigationPath } from '../models/navigation-path.model';
import { PathFile } from '../models/path-file.model';
import { PathQuery } from '../models/path-query.model';
import { QueryParameter } from '../models/query-parameter.model';
import { NavigationUtils } from '../navigation-utils';
import { NavigationMenuTypeEnum } from '../models/navigation-menu-type-enum';

export class UserNavToMenuItemSubConverter {

    constructor() { }

    public static convertToMenuItems(menuType: NavigationMenuTypeEnum, userNavigation: UserNavigation, activeLinkId: string, callback: (menuEvent: MenuItemCommandEvent) => void, useExtensionStubs: boolean, maxHosp: number, maxUnit: number, homeid: string, subscriptionPermIds: Array<string>, hospitalItems: Map<string, Array<MenuItem>>, unitItems: Map<string, Array<MenuItem>>): MenuItem[] {
        let menuItems = new Array<MenuItem>();
        menuItems = this.mapToMenuItems(userNavigation, callback, useExtensionStubs, maxHosp, maxUnit, homeid);
        const originalMenuItems: Array<MenuItem> = this.mapToMenuItems(userNavigation, callback, useExtensionStubs, maxHosp, maxUnit, homeid);

        this.populateMenuItemCaches(menuType, menuItems, userNavigation, activeLinkId, homeid, subscriptionPermIds, hospitalItems, unitItems);

        if (useExtensionStubs === true) {
            menuItems = this.addHospitalStubs(menuItems, userNavigation, callback, maxHosp, maxUnit, homeid, subscriptionPermIds, hospitalItems);
            menuItems = this.addUnitStubs(menuItems, originalMenuItems, userNavigation, activeLinkId, callback, maxHosp, maxUnit, homeid, hospitalItems, unitItems);
        }

        return menuItems;
    }

    private static populateMenuItemCaches(menuType: NavigationMenuTypeEnum, menuItems: MenuItem[], userNav: UserNavigation, activeLinkId: string, homeid: string, subscriptionPermIds: Array<string>, hospitalItems: Map<string, Array<MenuItem>>, unitItems: Map<string, Array<MenuItem>>) {
        const isFullMenu = (menuType === NavigationMenuTypeEnum.FullMenu);

        for (let index = 0; index < userNav.Items.length; index++) {
            const navItem = userNav.Items[index];
            const menuItem = menuItems[index];
            const { items } = menuItem;
            const isHomeItem = (navItem.PermItemID === homeid);

            if (isHomeItem === false) {
                if (subscriptionPermIds.includes(navItem.PermItemID)) {
                    const subId = NavigationUtils.GetSubscriptionIdFromPermId(navItem.PermItemID);
                    // Cache the Hospitals items if required
                    if (hospitalItems && items) {
                        const hospItemCache = hospitalItems.get(subId);
                        const isHospitalItemCachePopulated = ((hospItemCache === null) || (hospItemCache === undefined)) ? false : (hospItemCache.length > 0);
                        const isHospitalLevel = NavigationUtils.IsHospitalLevel(activeLinkId);

                        if (isFullMenu === true) {
                            // Only populate cache if not already populated (avoid overwriting with truncated data)
                            if (!isHospitalItemCachePopulated) {
                                hospitalItems.set(subId, items);
                            }
                            // Populate the Units items at the same time as caching the facility items
                            items.forEach(mi => {
                                // Cache the Unit items
                                if (unitItems) {
                                    const unitItemCache = unitItems.get(mi.id);
                                    const isUnitItemCachePopulated = ((unitItemCache === null) || (unitItemCache === undefined)) ? false : (unitItemCache.length > 0);
                                    if (!isUnitItemCachePopulated) {
                                        const filteredUnits = new Array<MenuItem>();
                                        const otherItems = new Array<MenuItem>();

                                        this.filterMenuItems(mi.items, filteredUnits, otherItems, '_u');
                                        unitItems.set(mi.id, filteredUnits);
                                    }
                                }
                            });
                        } else if ((!isHospitalItemCachePopulated) || isHospitalLevel === true) {
                            hospitalItems.set(subId, items);
                            items.forEach(mi => {
                                // Populate unit cache for the selected facility only
                                if (mi.id === activeLinkId) {
                                    // Cache the Unit items
                                    if (unitItems) {
                                        const filteredUnits = new Array<MenuItem>();
                                        const otherItems = new Array<MenuItem>();

                                        this.filterMenuItems(mi.items, filteredUnits, otherItems, '_u');
                                        const unitItemCache = unitItems.get(mi.id);
                                        const isUnitItemCachePopulated = ((unitItemCache === null) || (unitItemCache === undefined)) ? false : (unitItemCache.length > 0);

                                        if (!isUnitItemCachePopulated) {
                                            unitItems.set(mi.id, filteredUnits);
                                        }
                                    }
                                }
                            });
                        }
                    }
                }
            }
        }
    }

    public static convertToUserNavigation(menuItems: MenuItem[]): UserNavigation {
        const navItems = new Array<NavigationItem>();
        if (menuItems) {
            const itemIds = new Array<number>();
            menuItems.forEach(mi => {
                const ni = this.mapToNavigationItem(mi, itemIds, 0);
                navItems.push(ni);
            });
        }

        const userNavigation = new UserNavigation(navItems);
        return userNavigation;
    }

    private static getNextItemId(itemIds: Array<number>): number {
        let itemId: number;
        if (itemIds.length < 1) {
            itemId = 1;
        } else {
            itemId = itemIds[itemIds.length - 1] + 1;
        }
        return itemId;
    }

    private static mapToNavigationItem(menuItem: MenuItem, itemIds: Array<number>, parentID: number): NavigationItem {
        let navigationItem: NavigationItem;
        const itemId = this.getNextItemId(itemIds);
        itemIds.push(itemId);

        const navPath = this.mapToNavigationPath(menuItem);
        let navItems = new Array<NavigationItem>();
        if (menuItem.items) {
            const items = new Array<any>(...menuItem.items);
            items.forEach(mi => {
                const navItem = this.mapToNavigationItem(mi, itemIds, itemId);
                navItems.push(navItem);
            });
        }
        if (navItems.length < 1) {
            navItems = null;
        }
        navigationItem = new NavigationItem(itemId, '', menuItem.label, parentID, 0, 0, navPath, navItems);
        return navigationItem;
    }

    private static mapToNavigationPath(mi: MenuItem): NavigationPath {
        let navPath: NavigationPath;
        const command = mi.command;
        if (command) {
            let url: string;
            const cs: string = command.toString();
            const parts = cs.split('http');
            if (parts.length === 1) {
                const webserver = '';
                const commandParts = cs.split('/');
                commandParts.shift();
                const end = commandParts.join('/');
                const urlEnd = end.split("'");
                url = webserver + '/' + urlEnd[0];
                const dir = this.mapToDirectory(url);
                const pathFile = this.mapToPathFile(url);
                const pathQuery = this.mapToPathQuery(url);
                navPath = new NavigationPath(webserver, dir, pathFile, pathQuery);
            } else {
                const urlPlus = 'http' + parts.pop();
                const urlParts = urlPlus.split("'");
                url = urlParts[0];
                const webserver = this.mapToWebServer(url);
                const dir = this.mapToDirectory(url);
                const pathFile = this.mapToPathFile(url);
                const pathQuery = this.mapToPathQuery(url);
                navPath = new NavigationPath(webserver, dir, pathFile, pathQuery);
            }
        }
        return navPath;
    }
    private static mapToWebServer(url: string): string {
        const urlParts = url.split('/');
        const serverParts = urlParts.splice(0, 3);
        const server = serverParts.join('/');
        const webserver = server.split("'");
        return webserver.pop();
    }

    private static mapToDirectory(url: string): string {
        let dir: string;
        const urlParts = url.split('/');
        const lastPart = urlParts.pop();
        const fileNameExtIndex = lastPart.indexOf('.');
        if (fileNameExtIndex > 0) {
            const dirParts = urlParts.splice(3, url.length - 1);
            dir = dirParts.join('/');
        } else {
            const dirParts = url.split('?');
            dir = dirParts[0].substring(0 + 1, dirParts[0].length - 1);
        }
        return dir;
    }

    private static mapToPathFile(url: string): PathFile {
        let pathFile: PathFile;
        const urlParts = url.split('/');
        const last = urlParts.pop();
        const fileParts = last.split('.');
        if (fileParts && fileParts.length > 1) {
            const extPartsWithQuery = fileParts[1].split("'");
            const extParts = extPartsWithQuery[0].split('?');
            pathFile = new PathFile(fileParts[0], extParts[0]);
        }
        return pathFile;
    }

    private static mapToPathQuery(url: string): PathQuery {
        let pathQuery: PathQuery;
        const urlParts = url.split('?');
        if (urlParts && urlParts.length > 1) {
            const queryPairs = urlParts[1].split('&');
            const queryParams = new Array<QueryParameter>();
            queryPairs.forEach(pair => {
                const pairParts = pair.split('=');
                const qp = new QueryParameter(pairParts[0], pairParts[1]);
                queryParams.push(qp);
            });
            pathQuery = new PathQuery(queryParams);
        }
        return pathQuery;
    }

    private static mapToMenuItems(userNav: UserNavigation, callback: (menuEvent: MenuItemCommandEvent) => void, useExtensionStubs: boolean, maxHosp: number, maxUnit: number, homeid: string): MenuItem[] {
        const menuItems = new Array<MenuItem>();
        if (userNav) {
            userNav.Items.forEach(item => {
                const siblings = userNav.Items.length;
                const mi = this.mapToMenuItem(item, callback, useExtensionStubs, siblings, maxHosp, maxUnit, homeid);
                menuItems.push(mi);
            });
        }

        return menuItems;
    }

    private static addHospitalStubs(menuItems: MenuItem[], userNav: UserNavigation, callback: (menuEvent: MenuItemCommandEvent) => void, maxHosp: number, maxUnit: number, homeid: string, subscriptionPermIds: Array<string>, hospitalItems: Map<string, Array<MenuItem>>): MenuItem[] {
        const stubsAdded = new Array<string>();
        for (let index = 0; index < userNav.Items.length; index++) {
            const navItem = userNav.Items[index];
            const menuItem = menuItems[index];
            const isHomeItem = (navItem.PermItemID === homeid);
            const itemContainsStub = stubsAdded.includes(navItem.PermItemID);

            if ((isHomeItem === false) && (itemContainsStub === false)) {
                if (subscriptionPermIds.includes(navItem.PermItemID)) {

                    const stubsiblings = navItem.Items.length;
                    const isHospStub = this.isHospitalStubRequired(navItem, menuItem, true, stubsiblings, maxHosp, maxUnit, homeid, subscriptionPermIds);
                    if (isHospStub === true) {
                        const stub = this.getStubMenuItem(navItem, 'Facility Selector', 'stubhosp', callback, null);
                        menuItem.items = new Array<MenuItem>(stub);
                        stubsAdded.push(navItem.PermItemID);
                    } else {
                        navItem.Path.Directory = 'if/blankpage';
                    }
                }
            }
        }
        return menuItems;
    }

    private static filterMenuItems(items: MenuItem[], filteredItems: Array<MenuItem>, remainingItems: Array<MenuItem>, filter: string) {
        if (items) {
            items.forEach(item => {
                if (item) {
                    const id = item.id.toString().toLowerCase();
                    if (id.includes(filter)) {
                        filteredItems.push(item);
                    } else {
                        remainingItems.push(item);
                    }
                }
            });
        }
    }


    private static addUnitStub(menuItem: MenuItem, originalMenuItem: MenuItem, navItem: NavigationItem, activeLinkId: string, callback: (menuEvent: MenuItemCommandEvent) => void, maxHosp: number, maxUnit: number, homeid: string, hospItems: Map<string, Array<MenuItem>>, unitItems: Map<string, Array<MenuItem>>) {
        const usesHospitalStub = (menuItem && menuItem.items && menuItem.items[0]) ? NavigationUtils.IsStubFromPermId(menuItem.items[0].id) : false;
        let itemContainsStub = false;

        for (let hospindex = 0; hospindex < navItem.Items.length; hospindex++) {
            const hi = navItem.Items[hospindex];

             // Only add unit stub for the active facility
            const isFacility = NavigationUtils.IsLastPartHospitalItem(hi);
            // const isActiveFacility = hi.PermItemID === activeLinkId;
            const isActiveFacility = (activeLinkId && activeLinkId.length > 0) && activeLinkId.includes(hi.PermItemID);

            if (isFacility && isActiveFacility && hi.Items && hi.Items.length > 0) {
                let siblings = 0;
                const filteredUnits = new Array<MenuItem>();
                const otherItems = new Array<MenuItem>();
                const subId = NavigationUtils.GetSubscriptionIdFromPermId(activeLinkId);
                const hospItemCache = hospItems.get(subId);
                const originalHosp = (hospItemCache) ? hospItemCache[hospindex] : originalMenuItem.items[hospindex];

                if (unitItems) {
                    this.filterMenuItems(originalHosp.items, filteredUnits, otherItems, '_u');
                    siblings = filteredUnits.length;
                }

                let parentItem: MenuItem = null;
                if (usesHospitalStub) {
                    parentItem = menuItem.items[0];
                } else {
                    parentItem = menuItem.items.find(o => o.id === hi.PermItemID);
                }

                const hospChildItems = new Array<MenuItem>();
                const isUnitStubRequired = this.isUnitStubRequired(true, siblings, maxHosp, maxUnit);
                if (isUnitStubRequired) {
                    const uitems = (unitItems) ? unitItems.get(hi.PermItemID) : null;
                    const firstUnit = hi.Items.find(u => u.PermItemID.includes('_u'));
                    const stub = this.getStubMenuItem(firstUnit, 'Unit Selector', 'stubunit', callback, uitems);
                    itemContainsStub = true;
                    hospChildItems.push(stub);
                } else {
                    hospChildItems.push(...filteredUnits);
                }

                hospChildItems.push(...otherItems);
                parentItem.items = hospChildItems;
            }
        }
    }

    private static addUnitStubs(menuItems: MenuItem[], originalMenuItems: MenuItem[], userNav: UserNavigation, activeLinkId: string, callback: (menuEvent: MenuItemCommandEvent) => void, maxHosp: number, maxUnit: number, homeid: string, hospItems: Map<string, Array<MenuItem>>, unitItems: Map<string, Array<MenuItem>>): MenuItem[] {
        for (const navItem of userNav.Items) {
            const item = this.getMenuItem(menuItems, navItem.PermItemID);
            const originalItem = this.getMenuItem(originalMenuItems, navItem.PermItemID);
            this.addUnitStub(item, originalItem, navItem, activeLinkId, callback, maxHosp, maxUnit, homeid, hospItems, unitItems);
        }
        return menuItems;
    }

    private static isHospitalStubRequired(navItem: NavigationItem, childMenuItem: MenuItem,  useExtensionStubs: boolean, siblings: number, maxHosp: number, maxUnit: number, homeid: string, subscriptionPermIds: Array<string>): boolean {
        const isRequired = useExtensionStubs && subscriptionPermIds.includes(navItem.PermItemID) && siblings > maxHosp;
        return isRequired;
    }

    private static isUnitStubRequired(useExtensionStubs: boolean, siblings: number, maxHosp: number, maxUnit: number): boolean {
        const isRequired = ((useExtensionStubs === true) && (siblings > maxUnit));
        return isRequired;
    }

    private static getStubNavigationItem(navItem: NavigationItem, stubLabel: string, stubSuffix: string): NavigationItem {
        const stubPermId = navItem.PermItemID.replace(navItem.ItemID.toString(), "77777");
        const stub = new NavigationItem(navItem.ItemID, `${stubPermId}-${stubSuffix}`, stubLabel, navItem.ParentID, navItem.UnitCount, navItem.IndicatorCount, navItem.Path, new Array<NavigationItem>());
        return stub;
    }

    private static getStubMenuItem(navItem: NavigationItem, stubLabel: string, stubSuffix: string, callback: (menuEvent: MenuItemCommandEvent) => void, childItems: MenuItem[])  {
        const stubbedNav = this.getStubNavigationItem(navItem, stubLabel, stubSuffix);

        const miObj = NavigationItem.toMenuItemJSON(stubbedNav, callback);
        const stubMenuItem = miObj as MenuItem;
        return stubMenuItem;
    }

    private static mapToMenuItem(navItem: NavigationItem, callback: (menuEvent: MenuItemCommandEvent) => void, useExtensionStubs: boolean, siblings: number, maxHosp: number, maxUnit: number, homeid: string): MenuItem {
        let menuItem: MenuItem;
        const subMenuItems = new Array<MenuItem>();

        if (navItem && navItem.Items && navItem.Items.length > 0) {
            navItem.Items.forEach(ni => {
                const mi = this.mapToMenuItem(ni, callback, useExtensionStubs, navItem.Items.length, maxHosp, maxUnit, homeid);
                mi.visible = true;
                subMenuItems.push(mi);
            });
        }
        const miObj = NavigationItem.toMenuItemJSON(navItem, callback);
        menuItem = miObj as MenuItem;
        menuItem.visible = true;

        if (subMenuItems && subMenuItems.length > 0) {
            menuItem.items = subMenuItems;
        }
        return menuItem;
    }

    private static getSubMenuItem(menuItemId: string, subMenuItems: Array<any>): MenuItem {
        let mi: MenuItem;
        // tslint:disable-next-line:prefer-for-of
        const itemCount = (subMenuItems && subMenuItems.length) ? subMenuItems.length : 0;
        for (let index = 0; index < itemCount; index++) {
            const item = subMenuItems[index];
            if (item.id === menuItemId) {
                mi = item;
                break;
            }
            if (!mi && item.items) {
                mi = this.getSubMenuItem(menuItemId, item.items);
            }
        }
        return mi;
    }

    private static getMenuItem(items: any[], menuItemId: string): MenuItem {
        const mi = this.getSubMenuItem(menuItemId, items);
        return mi;
    }

}
